import { useState } from 'react';
import type { Order } from '@/services/orders';
import { Card } from './Card';
import { CARRIERS, CARRIER_SERVICES } from './constants';

interface ShippingTrackingCardProps {
  readonly order: Order;
  readonly onSave: (trackingNumber: string, carrier: string) => void;
  readonly isPending: boolean;
  readonly highlighted?: boolean;
}

export const ShippingTrackingCard = ({ order, onSave, isPending, highlighted }: ShippingTrackingCardProps) => {
  const existing = order.shipping;
  const existingTracking = existing?.trackingNumber ?? order.trackingNumber;
  const existingCarrier = existing?.carrier ?? order.trackingCarrier;

  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('usps');
  const [service, setService] = useState('Priority Mail');
  const [shipDate, setShipDate] = useState('');
  const [isEditing, setIsEditing] = useState(!existingTracking);

  const services = CARRIER_SERVICES[carrier] ?? ['Standard'];

  const handleSave = () => {
    const tn = trackingNumber.trim();
    if (!tn) return;
    onSave(tn, carrier);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setTrackingNumber(existingTracking ?? '');
    setCarrier(existingCarrier ?? 'usps');
    setService(existing?.shippingService ?? 'Priority Mail');
    setShipDate(existing?.dateShipped ?? '');
    setIsEditing(true);
  };

  return (
    <Card highlighted={highlighted}>
      <div style={{ fontSize: '14px', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: '10px' }}>
        Shipping & tracking
      </div>

      {/* Saved state */}
      {!isEditing && existingTracking && (
        <div>
          <div style={{ fontSize: '13px' }}>
            <TrackingRow label="Tracking" value={existingTracking} mono />
            {existingCarrier && <TrackingRow label="Carrier" value={existingCarrier.toUpperCase()} />}
            {existing?.shippingService && <TrackingRow label="Service" value={existing.shippingService} />}
            {existing?.dateShipped && <TrackingRow label="Ship date" value={new Date(existing.dateShipped).toLocaleDateString()} />}
            {existing?.deliveredDate && <TrackingRow label="Delivery date" value={new Date(existing.deliveredDate).toLocaleDateString()} />}
          </div>
          <div className="flex items-center" style={{ marginTop: '8px', gap: '12px' }}>
            {existingTracking && existingCarrier && (
              <a
                href={getTrackingUrl(existingCarrier, existingTracking)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '12px', color: 'var(--text-accent)', textDecoration: 'none' }}
              >
                Track package &rarr;
              </a>
            )}
            <span
              onClick={handleEdit}
              style={{ fontSize: '12px', color: 'var(--text-accent)', cursor: 'pointer' }}
            >
              Edit tracking
            </span>
          </div>
        </div>
      )}

      {/* Form state */}
      {isEditing && (
        <div>
          {!existingTracking && (
            <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
              No tracking info yet
            </div>
          )}

          {/* Row 1: Tracking + Carrier */}
          <div className="flex" style={{ gap: '6px', marginTop: '8px' }}>
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Tracking number"
              className="admin-input"
              style={{ flex: 1, fontSize: '12px' }}
            />
            <select
              value={carrier}
              onChange={(e) => {
                setCarrier(e.target.value);
                const newServices = CARRIER_SERVICES[e.target.value] ?? ['Standard'];
                setService(newServices[0]);
              }}
              className="admin-input"
              style={{ maxWidth: '100px', fontSize: '12px' }}
            >
              {CARRIERS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Row 2: Service + Ship date */}
          <div className="flex" style={{ gap: '6px', marginTop: '6px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                Service
              </label>
              <select
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="admin-input"
                style={{ width: '100%', fontSize: '12px' }}
              >
                {services.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                Ship date
              </label>
              <input
                type="date"
                value={shipDate}
                onChange={(e) => setShipDate(e.target.value)}
                className="admin-input"
                style={{ fontSize: '12px' }}
              />
            </div>
          </div>

          <div className="flex" style={{ marginTop: '8px', gap: '6px' }}>
            {existingTracking && (
              <button
                onClick={() => setIsEditing(false)}
                style={{
                  fontSize: '12px',
                  padding: '6px 14px',
                  minHeight: '36px',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  flex: 1,
                }}
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!trackingNumber.trim() || isPending}
              style={{
                fontSize: '12px',
                padding: '6px 14px',
                minHeight: '36px',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--text-primary)',
                color: 'var(--bg-surface)',
                cursor: !trackingNumber.trim() || isPending ? 'not-allowed' : 'pointer',
                fontWeight: 'var(--font-weight-medium)',
                opacity: !trackingNumber.trim() || isPending ? 0.5 : 1,
                flex: 1,
              }}
            >
              {isPending ? 'Saving...' : 'Save tracking'}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

const TRACKING_URLS: Record<string, string> = {
  usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
  ups: 'https://www.ups.com/track?tracknum=',
  fedex: 'https://www.fedex.com/fedextrack/?trknbr=',
  dhl: 'https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=',
};

const getTrackingUrl = (carrier: string, trackingNumber: string): string => {
  const base = TRACKING_URLS[carrier.toLowerCase()];
  if (!base) return '#';
  return `${base}${encodeURIComponent(trackingNumber)}`;
};

const TrackingRow = ({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}) => (
  <div
    className="flex justify-between"
    style={{
      padding: '4px 0',
      borderBottom: '0.5px solid var(--border-default)',
    }}
  >
    <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
    <span style={{ fontFamily: mono ? 'var(--font-mono)' : 'inherit', fontSize: mono ? '12px' : '13px' }}>
      {value}
    </span>
  </div>
);
